import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchAcceptanceInspection, fetchAcceptanceInspections } from '../../api/acceptanceInspection';
import { fetchFinalInspections } from '../../api/finalInspection';
import { fetchMidInspection, fetchMidInspections } from '../../api/midInspection';
import {
  acceptanceStatusLabel,
} from '../../types/acceptanceInspection';
import { finalInspectionStatusLabel } from '../../types/finalInspection';
import { midInspectionStatusLabel } from '../../types/midInspection';
import { InventoryItemKind } from '../../types/companyInventory';
import { reportError } from '../../utils/notify';
import '../equipment/EquipTabPanel.css';
import './InventoryStockHistory.css';
import './ProductInspectionsTab.css';

export type InspectionHistoryKind = 'ACCEPTANCE' | 'MID' | 'FINAL';

export interface InspectionHistoryRow {
  key: string;
  kind: InspectionHistoryKind;
  kindLabel: string;
  inspectionId: number;
  lotNo?: string | null;
  qty?: string | null;
  formTitle?: string | null;
  inspectorName?: string | null;
  inspectionDate?: string | null;
  status: string;
  statusLabel: string;
  overallJudgment?: string | null;
  itemName?: string | null;
  specText?: string | null;
  createdAt?: string | null;
}

interface Props {
  inventoryId: number | null | undefined;
  /** 재고 유형. 없으면 완제품(중간·제품검사 + 연계 인수)로 처리 */
  itemKind?: InventoryItemKind | string | null;
  emptyHint?: string;
}

function sortKey(row: InspectionHistoryRow) {
  return `${row.inspectionDate || row.createdAt || ''}#${row.inspectionId}`;
}

function toAcceptanceRow(row: {
  inspectionId: number;
  lotNo?: string | null;
  incomingQty?: string | null;
  formTitle?: string | null;
  inspectorName?: string | null;
  inspectionDate?: string | null;
  status: string;
  overallJudgment?: string | null;
  itemName?: string | null;
  productName?: string | null;
  specText?: string | null;
  createdAt?: string | null;
}): InspectionHistoryRow {
  return {
    key: `ACCEPTANCE-${row.inspectionId}`,
    kind: 'ACCEPTANCE',
    kindLabel: '인수검사',
    inspectionId: row.inspectionId,
    lotNo: row.lotNo,
    qty: row.incomingQty,
    formTitle: row.formTitle,
    inspectorName: row.inspectorName,
    inspectionDate: row.inspectionDate,
    status: row.status,
    statusLabel: acceptanceStatusLabel(row.status),
    overallJudgment: row.overallJudgment,
    itemName: row.itemName || row.productName,
    specText: row.specText,
    createdAt: row.createdAt,
  };
}

async function loadForParts(inventoryId: number): Promise<InspectionHistoryRow[]> {
  const list = await fetchAcceptanceInspections({ inventoryId });
  return list.map(toAcceptanceRow).sort((a, b) => sortKey(b).localeCompare(sortKey(a)));
}

async function loadForFinished(inventoryId: number): Promise<InspectionHistoryRow[]> {
  const [mids, finals] = await Promise.all([
    fetchMidInspections({ inventoryId }),
    fetchFinalInspections({ inventoryId }),
  ]);

  const midDetails = await Promise.all(
    mids.map((row) => fetchMidInspection(row.inspectionId).catch(() => null))
  );
  const acceptanceIds = Array.from(
    new Set(
      midDetails
        .flatMap((detail) => detail?.inputs ?? [])
        .map((input) => input.acceptanceInspectionId)
        .filter((id): id is number => id != null && id > 0)
    )
  );
  const acceptances = (
    await Promise.all(
      acceptanceIds.map((id) => fetchAcceptanceInspection(id).catch(() => null))
    )
  ).filter((row): row is NonNullable<typeof row> => row != null);

  const rows: InspectionHistoryRow[] = [
    ...acceptances.map(toAcceptanceRow),
    ...mids.map((row) => ({
      key: `MID-${row.inspectionId}`,
      kind: 'MID' as const,
      kindLabel: '중간검사',
      inspectionId: row.inspectionId,
      lotNo: row.lotNo,
      qty: row.lotQty,
      formTitle: row.formTitle,
      inspectorName: row.inspectorName,
      inspectionDate: row.inspectionDate,
      status: row.status,
      statusLabel: midInspectionStatusLabel(row.status),
      overallJudgment: row.overallJudgment,
      itemName: row.itemName,
      specText: row.specText,
      createdAt: row.createdAt,
    })),
    ...finals.map((row) => ({
      key: `FINAL-${row.inspectionId}`,
      kind: 'FINAL' as const,
      kindLabel: '제품검사',
      inspectionId: row.inspectionId,
      lotNo: row.lotNo,
      qty: row.lotQty,
      formTitle: row.formTitle,
      inspectorName: row.inspectorName,
      inspectionDate: row.inspectionDate,
      status: row.status,
      statusLabel: finalInspectionStatusLabel(row.status),
      overallJudgment: row.overallJudgment,
      itemName: row.itemName,
      specText: row.specText,
      createdAt: row.createdAt,
    })),
  ];

  return rows.sort((a, b) => sortKey(b).localeCompare(sortKey(a)));
}

function formatDate(value?: string | null) {
  if (!value) return '-';
  return value.replace('T', ' ').slice(0, 10);
}

function textOrDash(value?: string | null) {
  const v = value?.trim();
  return v ? v : '-';
}

function ProductInspectionsTab({ inventoryId, itemKind, emptyHint }: Props) {
  const [rows, setRows] = useState<InspectionHistoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [kindFilter, setKindFilter] = useState<'ALL' | InspectionHistoryKind>('ALL');

  const isPartsLike =
    itemKind === 'PART' || itemKind === 'MATERIAL';

  const load = useCallback(async () => {
    if (inventoryId == null || inventoryId <= 0) {
      setRows([]);
      setError('');
      return;
    }
    try {
      setLoading(true);
      const list = isPartsLike
        ? await loadForParts(inventoryId)
        : await loadForFinished(inventoryId);
      setRows(list);
      setError('');
    } catch {
      reportError(setError, '검사이력을 불러오지 못했습니다.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [inventoryId, isPartsLike]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(
    () => (kindFilter === 'ALL' ? rows : rows.filter((row) => row.kind === kindFilter)),
    [rows, kindFilter]
  );

  if (inventoryId == null || inventoryId <= 0) {
    return (
      <div className="equip-tab-panel product-inspections">
        <p className="equip-tab-panel__hint">
          {emptyHint ||
            '연동된 재고 품목이 없습니다. 완제품은 재고관리와 연결된 뒤 검사이력이 표시됩니다.'}
        </p>
      </div>
    );
  }

  return (
    <div className="equip-tab-panel product-inspections">
      <div className="equip-tab-panel__toolbar">
        <div>
          <h3>검사이력</h3>
          <p className="equip-tab-panel__hint" style={{ margin: '4px 0 0' }}>
            {isPartsLike
              ? '이 품목의 인수검사 성적서를 모아서 보여줍니다.'
              : '중간·제품검사와, 중간검사에 투입된 인수검사 Lot을 함께 보여줍니다.'}
          </p>
        </div>
        <button type="button" className="is-ghost" onClick={() => void load()} disabled={loading}>
          새로고침
        </button>
      </div>

      {!isPartsLike && rows.length > 0 ? (
        <div className="product-inspections__filters">
          {(
            [
              ['ALL', '전체'],
              ['ACCEPTANCE', '인수'],
              ['MID', '중간'],
              ['FINAL', '제품'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={kindFilter === value ? 'is-active' : ''}
              onClick={() => setKindFilter(value)}
            >
              {label}
              <span>
                {value === 'ALL'
                  ? rows.length
                  : rows.filter((row) => row.kind === value).length}
              </span>
            </button>
          ))}
        </div>
      ) : null}

      {error && <p className="equip-tab-panel__error">{error}</p>}
      {loading ? (
        <p className="equip-tab-panel__hint">불러오는 중...</p>
      ) : filtered.length === 0 ? (
        <p className="equip-tab-panel__hint">
          {rows.length === 0
            ? '아직 검사이력이 없습니다. 관련 검사가 등록·승인되면 여기에 표시됩니다.'
            : '선택한 유형의 검사가 없습니다.'}
        </p>
      ) : (
        <div className="stock-history__table-wrap">
          <table className="stock-history__table product-inspections__table">
            <thead>
              <tr>
                <th>유형</th>
                <th>품명</th>
                <th>규격</th>
                <th>검사일</th>
                <th>Lot</th>
                <th>수량</th>
                <th>양식</th>
                <th>검사자</th>
                <th>상태</th>
                <th>판정</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.key}>
                  <td>
                    <span className={`product-inspections__kind is-${row.kind.toLowerCase()}`}>
                      {row.kindLabel}
                    </span>
                  </td>
                  <td className={row.itemName?.trim() ? undefined : 'is-muted'}>
                    {textOrDash(row.itemName)}
                  </td>
                  <td>
                    {row.specText?.trim() ? (
                      <span className="product-inspections__spec">{row.specText.trim()}</span>
                    ) : (
                      <span className="is-muted">기본</span>
                    )}
                  </td>
                  <td>{formatDate(row.inspectionDate)}</td>
                  <td className={row.lotNo ? undefined : 'is-muted'}>{row.lotNo || '-'}</td>
                  <td>{row.qty?.trim() || '-'}</td>
                  <td className={row.formTitle ? undefined : 'is-muted'}>
                    {row.formTitle || '-'}
                  </td>
                  <td className={row.inspectorName ? undefined : 'is-muted'}>
                    {row.inspectorName || '-'}
                  </td>
                  <td>{row.statusLabel}</td>
                  <td className={row.overallJudgment ? undefined : 'is-muted'}>
                    {row.overallJudgment || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ProductInspectionsTab;
